import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SALES_PHONE, COMPANY } from "@/data/site";

const Logo = ({ dark = false }) => (
  <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label={`${COMPANY.name} — Home`}>
    <img
      src="/ambrotech-emblem.png"
      alt="Ambrotech Private Limited logo"
      className="h-11 w-11 rounded-md bg-white object-contain p-0.5"
      width="44"
      height="44"
    />
    <span className="leading-none">
      <span className={`block font-display text-lg font-bold tracking-[0.08em] ${dark ? "text-white" : "text-navy"}`}>
        AMBROTECH
      </span>
      <span className={`mt-1 block text-[10px] font-medium tracking-[0.3em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
        PRIVATE LIMITED
      </span>
    </span>
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative text-sm font-semibold transition-colors duration-200 hover:text-navy ${
      isActive ? "text-navy after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-full after:bg-brand after:content-['']" : "text-slate-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur-md transition-shadow ${
        scrolled ? "border-slate-200 shadow-[0_1px_16px_rgba(11,25,44,0.07)]" : "border-slate-100"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.path} to={l.path} className={linkClass} data-testid={`nav-link-${l.path === "/" ? "home" : l.path.slice(1)}`}>
              {l.name}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SALES_PHONE.tel}
            className="hidden items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-brand hover:text-brand md:inline-flex"
            data-testid="header-sales-phone-link"
          >
            <Phone className="h-4 w-4" strokeWidth={2.2} />
            {SALES_PHONE.label}
          </a>
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-navy lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden border-t border-slate-100 bg-white transition-[max-height] duration-300 lg:hidden ${
          open ? "max-h-[420px]" : "max-h-0 border-t-0"
        }`}
      >
        <nav className="flex flex-col gap-1 px-5 py-4" aria-label="Mobile navigation">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              className={({ isActive }) =>
                `rounded-md px-3 py-2.5 text-sm font-semibold ${
                  isActive ? "bg-slate-50 text-navy" : "text-slate-600"
                }`
              }
              data-testid={`mobile-nav-link-${l.path === "/" ? "home" : l.path.slice(1)}`}
            >
              {l.name}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;